
import { saveAs } from 'file-saver';
import { degrees, LineCapStyle, PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { AnnotationDrawingPlanEntity, DrawingPlanPDFEntity, PdfHighlighterEntity, PointsEntity } from '../../../interface/pdf-pin';
import { v4 as uuid4 } from "uuid"
import { MapEntity } from '../../../store/pdf-pin.store';
import { IsNullOrUndefined, IsStringNullEmptyOrUndefined } from '../../../utils/null-check';
import { SolutionDetails } from '../../../interface/order';



export const FINAL_COLOR = '#1E90FF';
export const PREVIEW_COLOR = '#555555';
export const LINK_COLOR = "#daab34";


function hexToRgb(hex: string) {
    const sanitized = hex.replace('#', '');
    let r, g, b, a;

    if (sanitized.length === 6) {
        r = parseInt(sanitized.substring(0, 2), 16);
        g = parseInt(sanitized.substring(2, 4), 16);
        b = parseInt(sanitized.substring(4, 6), 16);
        a = 255;
    } else if (sanitized.length === 8) {
        r = parseInt(sanitized.substring(0, 2), 16);
        g = parseInt(sanitized.substring(2, 4), 16);
        b = parseInt(sanitized.substring(4, 6), 16);
        a = parseInt(sanitized.substring(6, 8), 16);
    } else {
        throw new Error("Invalid hex color");
    }

    return {
        rgb: rgb(r / 255, g / 255, b / 255),
        opacity: a / 255,
    };
}


export function shortUUID(digits = 5) {
    const fullUUID = uuid4().replace(/-/g, '');
    const intValue = parseInt(fullUUID.slice(0, 12), 16); // large int from first few hex chars
    return String(intValue).slice(-digits);
}


/* Get coordinates for placed icon on highlight  */
export function getBestSegmentForIcon(points: { x: number | null; y: number | null }[]) {
    if (points.length < 2) return null;


    const segments: { start: { x: number, y: number }, end: { x: number, y: number } }[] = [];
    let segmentStartIndex = 0;

    for (let i = 1; i < points.length; i++) {
        if (points[i].x == null || points[i].y == null) {

            if (i - 1 > segmentStartIndex) {
                for (let j = segmentStartIndex; j < i - 1; j++) {
                    segments.push({ start: points[j] as { x: number, y: number }, end: points[j + 1] as { x: number, y: number } });
                }
            }
            segmentStartIndex = i + 1;
        }
    }

    if (segmentStartIndex < points.length - 1) {
        for (let j = segmentStartIndex; j < points.length - 1; j++) {
            segments.push({ start: points[j] as { x: number, y: number }, end: points[j + 1] as { x: number, y: number } });
        }
    }

    if (segments.length === 0) return null;


    const validPoints = points.filter(p => p.x != null && p.y != null) as { x: number, y: number }[];
    const centroid = validPoints.reduce(
        (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
        { x: 0, y: 0 }
    );
    centroid.x /= validPoints.length;
    centroid.y /= validPoints.length;

    // Find longest segment length
    let maxLen = 0;
    segments.forEach(seg => {
        const dx = seg.end.x - seg.start.x;
        const dy = seg.end.y - seg.start.y;
        const len = Math.hypot(dx, dy);
        if (len > maxLen) maxLen = len;
    });


    const nearMaxSegments = segments.filter(seg => {
        const dx = seg.end.x - seg.start.x;
        const dy = seg.end.y - seg.start.y;
        const len = Math.hypot(dx, dy);
        return len >= 0.9 * maxLen;
    });


    let bestSeg = nearMaxSegments[0];
    let bestDist = Infinity;
    for (const seg of nearMaxSegments) {
        const midX = (seg.start.x + seg.end.x) / 2;
        const midY = (seg.start.y + seg.end.y) / 2;
        const dist = Math.hypot(midX - centroid.x, midY - centroid.y);
        if (dist < bestDist) {
            bestDist = dist;
            bestSeg = seg;
        }
    }

    return bestSeg;
}

export function resizeSvg(
    svgText: string,
    newWidth: number,
    newHeight: number,
    fillColor?: string
): string {
    let updated = svgText;

    // --- Extract current width/height (with or without units) ---
    const widthMatch = svgText.match(/width="([\d.]+)([a-z%]*)"/i);
    const heightMatch = svgText.match(/height="([\d.]+)([a-z%]*)"/i);

    const origWidth = widthMatch ? parseFloat(widthMatch[1]) : newWidth;
    const origHeight = heightMatch ? parseFloat(heightMatch[1]) : newHeight;

    // --- Update width & height attributes ---
    updated = updated.replace(/width="[^"]*"/i, `width="${newWidth}"`);
    updated = updated.replace(/height="[^"]*"/i, `height="${newHeight}"`);

    // --- Ensure viewBox exists ---
    const viewBoxMatch = updated.match(/viewBox="([^"]+)"/i);
    if (!viewBoxMatch) {
        // Add a viewBox based on original width/height
        updated = updated.replace(
            /<svg([^>]*)>/i,
            `<svg$1 viewBox="0 0 ${origWidth} ${origHeight}">`
        );
    }

    // --- Optional recoloring ---
    if (fillColor) {
        // Replace inline fill="..." except "none"
        updated = updated.replace(
            /fill="(?!none)[^"]*"/g,
            `fill="${fillColor}"`
        );

        // Replace stroke="..." except "none"
        updated = updated.replace(
            /stroke="(?!none)[^"]*"/g,
            `stroke="${fillColor}"`
        );

        // Handle inline style fills/strokes
        updated = updated.replace(
            /fill:\s*#[0-9A-Fa-f]{3,6}/g,
            `fill:${fillColor}`
        );
        updated = updated.replace(
            /fill:\s*(rgb|rgba)\([^)]+\)/g,
            `fill:${fillColor}`
        );
        updated = updated.replace(
            /stroke:\s*#[0-9A-Fa-f]{3,6}/g,
            `stroke:${fillColor}`
        );
        updated = updated.replace(
            /stroke:\s*(rgb|rgba)\([^)]+\)/g,
            `stroke:${fillColor}`
        );

        // Handle <style> blocks (class-based)
        updated = updated.replace(
            /\.fil[0-9]+\s*\{[^}]*fill:\s*#[0-9A-Fa-f]{3,6};?/g,
            (match) => match.replace(/fill:\s*#[0-9A-Fa-f]{3,6};?/, `fill:${fillColor};`)
        );

        updated = updated.replace(
            /\.fil[0-9]+\s*\{[^}]*fill:\s*(rgb|rgba)\([^)]+\);?/g,
            (match) => match.replace(/fill:\s*(rgb|rgba)\([^)]+\);?/, `fill:${fillColor};`)
        );


    }

    return updated;
}

export function recolorSvg(svgText: string, fillColor: string) {
    let coloredSvg = svgText;

    // === Replace inline fill (hex, rgb/rgba) ===
    coloredSvg = coloredSvg.replace(
        /fill:\s*#[0-9A-Fa-f]{3,6}/g,
        `fill:${fillColor}`
    );
    coloredSvg = coloredSvg.replace(
        /fill:\s*(rgb|rgba)\([^)]+\)/g,
        `fill:${fillColor}`
    );

    // === Replace inline stroke (hex, rgb/rgba) ===
    coloredSvg = coloredSvg.replace(
        /stroke:\s*#[0-9A-Fa-f]{3,6}/g,
        `stroke:${fillColor}`
    );
    coloredSvg = coloredSvg.replace(
        /stroke:\s*(rgb|rgba)\([^)]+\)/g,
        `stroke:${fillColor}`
    );

    // === Replace fill="..." attributes (but not fill="none") ===
    coloredSvg = coloredSvg.replace(
        /fill="(?!none)[^"]+"/g,
        `fill="${fillColor}"`
    );

    // === Replace stroke="..." attributes (but not stroke="none") ===
    coloredSvg = coloredSvg.replace(
        /stroke="(?!none)[^"]+"/g,
        `stroke="${fillColor}"`
    );

    // === Replace CSS inside <style> blocks ===
    coloredSvg = coloredSvg.replace(
        /\.fil[0-9]+\s*\{[^}]*fill:\s*#[0-9A-Fa-f]{3,6};?/g,
        (match) => match.replace(/fill:\s*#[0-9A-Fa-f]{3,6};?/, `fill:${fillColor};`)
    );

    coloredSvg = coloredSvg.replace(
        /\.fil[0-9]+\s*\{[^}]*fill:\s*(rgb|rgba)\([^)]+\);?/g,
        (match) => match.replace(/fill:\s*(rgb|rgba)\([^)]+\);?/, `fill:${fillColor};`)
    );

    coloredSvg = coloredSvg.replace(
        /\.str[0-9]+\s*\{[^}]*stroke:\s*#[0-9A-Fa-f]{3,6};?/g,
        (match) => match.replace(/stroke:\s*#[0-9A-Fa-f]{3,6};?/, `stroke:${fillColor};`)
    );

    coloredSvg = coloredSvg.replace(
        /\.str[0-9]+\s*\{[^}]*stroke:\s*(rgb|rgba)\([^)]+\);?/g,
        (match) =>
            match.replace(/stroke:\s*(rgb|rgba)\([^)]+\);?/, `stroke:${fillColor};`)
    );

    console.log(fillColor, "fillColor")

    return coloredSvg;
}


export const svgUrlToPngBytes = async (
    svgUrl: string,
    fillColor: string,
    width = 100,
    height = 100
): Promise<Uint8Array> => {
    let svgText: string | null = null;

    try {
        const res = await fetch(svgUrl);
        if (res.ok) {
            svgText = await res.text();
        }
    } catch (e) {
        console.warn("⚠️ Failed to fetch SVG:", e);
    }

    if (!svgText) {
        // fallback: return empty PNG (transparent canvas)
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    const reader = new FileReader();
                    reader.onloadend = () =>
                        resolve(new Uint8Array(reader.result as ArrayBuffer));
                    reader.readAsArrayBuffer(blob);
                } else {
                    resolve(new Uint8Array());
                }
            }, "image/png");
        });
    }

    let coloredSvg = resizeSvg(recolorSvg(svgText, fillColor), width, height);

    return new Promise((resolve) => {
        const img = new Image();
        const blob = new Blob([coloredSvg], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);

        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx?.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
                if (blob) {
                    const reader = new FileReader();
                    reader.onloadend = () =>
                        resolve(new Uint8Array(reader.result as ArrayBuffer));
                    reader.readAsArrayBuffer(blob);
                } else {
                    resolve(new Uint8Array());
                }
            }, "image/png");
        };

        img.onerror = () => {
            console.warn("⚠️ Failed to load SVG image.");
            resolve(new Uint8Array()); // fallback to empty
        };

        img.src = url;
    });
};





const perpendicularDistance = (point: { x: number; y: number }, start: { x: number; y: number }, end: { x: number; y: number }): number => {
    const A = point.x - start.x;
    const B = point.y - start.y;
    const C = end.x - start.x;
    const D = end.y - start.y;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;

    if (lenSq === 0) return Math.sqrt(A * A + B * B);

    const param = dot / lenSq;
    let xx, yy;

    if (param < 0) {
        xx = start.x;
        yy = start.y;
    } else if (param > 1) {
        xx = end.x;
        yy = end.y;
    } else {
        xx = start.x + param * C;
        yy = start.y + param * D;
    }

    const dx = point.x - xx;
    const dy = point.y - yy;
    return Math.sqrt(dx * dx + dy * dy);
};


export const simplifyPathFromPolygraph = (points: { x: number; y: number }[], tolerance: number = 5): { x: number; y: number }[] => {
    if (points.length <= 2) return points;

    const douglasPeucker = (points: { x: number; y: number }[], epsilon: number): { x: number; y: number }[] => {
        if (points.length <= 2) return points;

        let maxDistance = 0;
        let maxIndex = 0;
        const start = points[0];
        const end = points[points.length - 1];

        for (let i = 1; i < points.length - 1; i++) {
            const distance = perpendicularDistance(points[i], start, end);
            if (distance > maxDistance) {
                maxDistance = distance;
                maxIndex = i;
            }
        }

        if (maxDistance > epsilon) {
            const leftSegment = douglasPeucker(points.slice(0, maxIndex + 1), epsilon);
            const rightSegment = douglasPeucker(points.slice(maxIndex), epsilon);
            return [...leftSegment.slice(0, -1), ...rightSegment];
        } else {
            return [start, end];
        }
    };

    return douglasPeucker(points, tolerance);
};


function isValidPoint(p?: { x?: number; y?: number }) {
    return p && typeof p.x === "number" && typeof p.y === "number";
}

function segmentsIntersect(p1: any, p2: any, p3: any, p4: any) {
    if (!isValidPoint(p1) || !isValidPoint(p2) || !isValidPoint(p3) || !isValidPoint(p4)) {
        return false;
    }
    const d = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
    if (d === 0) return false; // parallel lines

    const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / d;
    const ub = ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) / d;

    return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
}

function distancePointToSegment(p: any, v: any, w: any) {
    if (!isValidPoint(p) || !isValidPoint(v) || !isValidPoint(w)) {
        return Infinity;
    }

    const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
    if (l2 === 0) return Math.hypot(p.x - v.x, p.y - v.y);
    let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(p.x - (v.x + t * (w.x - v.x)), p.y - (v.y + t * (w.y - v.y)));
}

function distanceBetweenSegments(p1: any, p2: any, p3: any, p4: any) {
    if (segmentsIntersect(p1, p2, p3, p4)) return 0;
    return Math.min(
        distancePointToSegment(p1, p3, p4),
        distancePointToSegment(p2, p3, p4),
        distancePointToSegment(p3, p1, p2),
        distancePointToSegment(p4, p1, p2)
    );
}

function distanceBetweenSegmentsDetailed(p1: any, p2: any, p3: any, p4: any) {
    const dist = distanceBetweenSegments(p1, p2, p3, p4);

    // If the minimum distance comes from endpoints only
    const dists = [
        distance(p1, p3),
        distance(p1, p4),
        distance(p2, p3),
        distance(p2, p4),
    ];
    const minEndpointDist = Math.min(...dists);

    const isEndpointOnly = Math.abs(dist - minEndpointDist) < 1e-6;

    return { dist, isEndpointOnly };
}

function distance(a: any, b: any) {
    return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

export function checkStrokeOverlap(
    stroke: { points: { x: number; y: number }[]; width: number },
    existingStrokes: { id: string; points: { x: number; y: number }[]; width: number }[],
    overlapThreshold = 1
) {
    if (!stroke?.points?.length) return [];

    const overlaps: any[] = [];
    const threshold = Math.max(1, overlapThreshold);

    for (const existing of existingStrokes) {
        if (!existing.points?.length) continue;

        let intersects = false;
        let minDist = Infinity;

        for (let i = 0; i < stroke.points.length - 1; i++) {
            const p1 = stroke.points[i];
            const p2 = stroke.points[i + 1];

            if (!isValidPoint(p1) || !isValidPoint(p2)) continue;

            for (let j = 0; j < existing.points.length - 1; j++) {
                const p3 = existing.points[j];
                const p4 = existing.points[j + 1];

                if (!isValidPoint(p3) || !isValidPoint(p4)) continue;

                const { dist, isEndpointOnly } = distanceBetweenSegmentsDetailed(p1, p2, p3, p4);
                minDist = Math.min(minDist, dist);

                if (dist <= threshold && !isEndpointOnly) {
                    intersects = true;
                }
            }
        }

        console.log({
            existingId: existing.id,
            dist: minDist,
            intersects
        });

        if (intersects) overlaps.push({ targetStroke: existing });
    }

    return overlaps;
}



export const distanceToSegment = (p: { x: number, y: number }, v: { x: number, y: number }, w: { x: number, y: number }) => {
    const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
    if (l2 === 0) return Math.hypot(p.x - v.x, p.y - v.y); // v==w
    let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
    t = Math.max(0, Math.min(1, t)); // clamp t to [0,1]
    const projX = v.x + t * (w.x - v.x);
    const projY = v.y + t * (w.y - v.y);
    return Math.hypot(p.x - projX, p.y - projY);
}

const getRotatedCoordinates = (
    px: number,
    py: number,
    width: number,
    height: number,
    rotation: number
): { x: number, y: number } => {
    if (rotation === 90) {
        return {
            x: (py / 100) * width,
            y: (px / 100) * height
        };
    } else if (rotation === 180) {
        return {
            x: width - (px / 100) * width,
            y: (py / 100) * height
        };
    } else if (rotation === 270) {
        return {
            x: width - (py / 100) * width,
            y: height - (px / 100) * height
        };
    } else {
        let x = (px / 100) * width;
        let y = (py / 100) * height;
        y = height - y;
        return { x, y };
    }
};


function expandToAspectRatio(minX: number, minY: number, maxX: number, maxY: number, aspectW = 4, aspectH = 3, pageWidth: number, pageHeight: number) {
    const boxW = maxX - minX;
    const boxH = maxY - minY;
    const boxRatio = boxW / boxH;
    const targetRatio = aspectW / aspectH;

    let newW = boxW;
    let newH = boxH;

    if (boxRatio > targetRatio) {
        // too wide, increase height
        newH = boxW / targetRatio;
    } else {
        // too tall, increase width
        newW = boxH * targetRatio;
    }

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    let newMinX = centerX - newW / 2;
    let newMinY = centerY - newH / 2;

    // Clamp to page boundaries
    newMinX = Math.max(0, newMinX);
    newMinY = Math.max(0, newMinY);
    let newMaxX = Math.min(pageWidth, newMinX + newW);
    let newMaxY = Math.min(pageHeight, newMinY + newH);

    return {
        minX: newMinX,
        minY: newMinY,
        maxX: newMaxX,
        maxY: newMaxY,
        width: newMaxX - newMinX,
        height: newMaxY - newMinY,
    };
}

function interpolateLinePoints(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    step = 0.5
): { x: number; y: number }[] {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const count = Math.max(2, Math.ceil(dist / step));
    return Array.from({ length: count + 1 }, (_, i) => ({
        x: p1.x + (dx * i) / count,
        y: p1.y + (dy * i) / count,
    }));
}

function getBestSegment(points: { x: number | null; y: number | null }[]) {
    if (points.length < 2) return null;

    // Extract continuous segments separated by null points
    const segments: { start: { x: number; y: number }, end: { x: number; y: number } }[] = [];
    let segmentStart = 0;

    for (let i = 1; i < points.length; i++) {
        if (points[i].x == null || points[i].y == null) {
            if (i - 1 > segmentStart) {
                for (let j = segmentStart; j < i - 1; j++) {
                    segments.push({ start: points[j] as { x: number; y: number }, end: points[j + 1] as { x: number; y: number } });
                }
            }
            segmentStart = i + 1;
        }
    }
    if (segmentStart < points.length - 1) {
        for (let j = segmentStart; j < points.length - 1; j++) {
            segments.push({ start: points[j] as { x: number; y: number }, end: points[j + 1] as { x: number; y: number } });
        }
    }

    if (segments.length === 0) return null;

    // Calculate centroid of all valid points
    const validPoints = points.filter(p => p.x != null && p.y != null) as { x: number; y: number }[];
    const centroid = validPoints.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
    centroid.x /= validPoints.length;
    centroid.y /= validPoints.length;

    // Find max length
    let maxLen = 0;
    segments.forEach(seg => {
        const dx = seg.end.x - seg.start.x;
        const dy = seg.end.y - seg.start.y;
        const len = Math.hypot(dx, dy);
        if (len > maxLen) maxLen = len;
    });

    // Get all segments within 90% of max length
    const nearMax = segments.filter(seg => {
        const dx = seg.end.x - seg.start.x;
        const dy = seg.end.y - seg.start.y;
        return Math.hypot(dx, dy) >= 0.9 * maxLen;
    });

    // Pick the segment whose midpoint is closest to centroid
    let bestSeg = nearMax[0];
    let bestDist = Infinity;
    for (const seg of nearMax) {
        const midX = (seg.start.x + seg.end.x) / 2;
        const midY = (seg.start.y + seg.end.y) / 2;
        const dist = Math.hypot(midX - centroid.x, midY - centroid.y);
        if (dist < bestDist) {
            bestDist = dist;
            bestSeg = seg;
        }
    }

    return bestSeg;
}

function drawNumberIcon(page: any, {
    x,
    y,
    number,
    size = 20,
    font,
    color,
    borderWidth = 1,
    rotation = 0
}: any) {
    const radius = size / 2;

    // Circle
    page.drawCircle({
        x,
        y,
        size: radius,
        borderColor: color,
        borderWidth,
        borderOpacity: 0.9,
        color: color,
        opacity: 0.9,
        rotate: degrees(rotation),
    });

    // Text inside
    const text = String(number);
    const textSize = size * 0.5;
    const textWidth = font.widthOfTextAtSize(text, textSize);
    const textHeight = font.heightAtSize(textSize);



    page.drawText(text, {
        x: x - textWidth / 2,
        y: y - textHeight / 4,
        size: textSize,
        font,
        color: rgb(1, 1, 1),
        opacity: 1,
        rotate: degrees(rotation),
    });
}

export const downloadKeyPointForStroke = async (pdfUrl: string, stroke: PdfHighlighterEntity[], solutionList: SolutionDetails[] | [], scale = 1, isFileDownload = true) => {
    try {
        const pdfBytes = await fetch(pdfUrl).then(res => res.arrayBuffer());
        const pdfDoc = await PDFDocument.load(pdfBytes);
        const pages = pdfDoc.getPages();


        const { width: pageWidth, height: pageHeight } = pages[0].getSize();
        const rotation = pages[0].getRotation().angle;


        const absPoints = stroke[0].points
            .filter(pt => pt.x != null && pt.y != null)
            .map(pt => getRotatedCoordinates(pt.x, pt.y, pageWidth, pageHeight, rotation));


        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        absPoints.forEach(p => {
            if (p.x < minX) minX = p.x;
            if (p.y < minY) minY = p.y;
            if (p.x > maxX) maxX = p.x;
            if (p.y > maxY) maxY = p.y;
        });

        // Add padding
        const padding = 60;
        minX -= padding;
        minY -= padding;
        maxX += padding;
        maxY += padding;

        let aspectW = rotation === 0 ? 6 : 4;
        let aspectH = rotation === 0 ? 4 : 6;
        // Expand to 4:3 crop and center
        const crop = expandToAspectRatio(minX, minY, maxX, maxY, aspectW, aspectH, pageWidth, pageHeight);

        // ---- Strokes ----
        const strokesByPage = stroke.reduce<Record<number, PdfHighlighterEntity[]>>((acc, s) => {
            const pageNum = 0;
            if (!acc[pageNum]) {
                acc[pageNum] = [];
            }
            acc[pageNum].push(s);
            return acc;
        }, {});

        const strokeOpacity = 0.5; // lower to make intersections lighter
        const strokeSegments = 1; // reduce from 100 to avoid overdraw

        for await (const [pageNumStr, pageStrokes] of Object.entries(strokesByPage)) {
            const page = pages[parseInt(pageNumStr)];
            if (!page) continue;

            const { width, height } = page.getSize();
            const rotation = page.getRotation().angle;

            for await (const s of pageStrokes) {

                const svgIconUrl = s.systemIcon;


                const absPoints = s.points.map((pt) =>
                    pt.x == null || pt.y == null
                        ? null
                        : getRotatedCoordinates(pt.x, pt.y, width, height, rotation)
                );

                const strokeWidth = s.width * scale;
                const { rgb: pdfStrokeColor } = hexToRgb(s.color);
                const jointRadius = (strokeWidth * 0.8) / 2;

                // === Draw stroke lines ===
                for (let i = 0; i < absPoints.length - 1; i++) {
                    const p1 = absPoints[i];
                    const p2 = absPoints[i + 1];
                    if (!p1 || !p2) continue;

                    const segments = interpolateLinePoints(p1, p2, strokeSegments);

                    for (let j = 0; j < segments.length - 1; j++) {
                        page.drawLine({
                            start: segments[j],
                            end: segments[j + 1],
                            color: pdfStrokeColor,
                            opacity: strokeOpacity ?? 1,
                            thickness: strokeWidth || 1,
                            lineCap: LineCapStyle.Projecting,
                        });
                    }
                }


                try {


                    // === ICON PLACEMENT 
                    if (s.points.length >= 2) {
                        // Usage inside your forEach stroke:
                        const bestSeg = getBestSegment(s.points);
                        if (bestSeg) {
                            const startAbs = getRotatedCoordinates(bestSeg.start.x, bestSeg.start.y, width, height, rotation);
                            const endAbs = getRotatedCoordinates(bestSeg.end.x, bestSeg.end.y, width, height, rotation);

                            const midX = (startAbs.x + endAbs.x) / 2;
                            const midY = (startAbs.y + endAbs.y) / 2;

                            const dx = endAbs.x - startAbs.x;
                            const dy = endAbs.y - startAbs.y;

                            const perpX = -dy;
                            const perpY = dx;
                            const lenPerp = Math.hypot(perpX, perpY);
                            const unitPerpX = perpX / lenPerp;
                            const unitPerpY = perpY / lenPerp;

                            const iconOffset = rotation === 0 ? 24 : 0;
                            const iconX = midX + unitPerpX * iconOffset;
                            const iconY = midY + unitPerpY * iconOffset;

                            if (s.solutionId && !IsStringNullEmptyOrUndefined(s.solutionId)) {
                                const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
                                const { rgb: pdfStrokeColor } = hexToRgb(s.color);
                                const solutionDetail = solutionList.find((item) => item._id === s.solutionId);
                                if (solutionDetail && !IsNullOrUndefined(solutionDetail)) {
                                    drawNumberIcon(page, {
                                        x: iconX,
                                        y: iconY,
                                        number: solutionDetail.solutionNo,
                                        font,
                                        size: 20,
                                        color: pdfStrokeColor,
                                        borderWidth: 2,
                                        rotation,
                                    });
                                }
                            } else {
                                const iconPngBytes = await svgUrlToPngBytes(svgIconUrl, s.color, 60, 60);
                                const iconImage = await pdfDoc.embedPng(iconPngBytes);
                                const iconDims = iconImage.scale(0.4);

                                page.drawImage(iconImage, {
                                    x: iconX - iconDims.width / 2,
                                    y: iconY - iconDims.height / 2,
                                    width: iconDims.width,
                                    height: iconDims.height,
                                    rotate: degrees(rotation),
                                });
                            }

                        }

                    }
                } catch (error) {
                    console.log(error)
                }
            };
        }

        let solutionName = ""
        const solutionDetail = solutionList.find((item) => (stroke[0] && stroke[0].solutionId) === item._id.toString());

        if (solutionDetail && !IsNullOrUndefined(solutionDetail)) {
            solutionName = [solutionDetail.solutionNo.toString(), solutionDetail.floor, solutionDetail.space].join("_").replace(/[\s,’ ’]/g, "-");
        }

        const newPdf = await PDFDocument.create();
        const [copiedPage] = await newPdf.copyPages(pdfDoc, [0]);
        copiedPage.setCropBox(crop.minX, crop.minY, crop.width, crop.height);
        newPdf.addPage(copiedPage);
        const modifiedBytes: any = await newPdf.save();
        const blob = new Blob([modifiedBytes], { type: 'application/pdf' });
        const originalFilename = pdfUrl.split('/').pop() || 'document';
        const filename = (solutionName || originalFilename).replace('.pdf', '-with-highlights.pdf');
        const file = new File([blob], filename, { type: 'application/pdf' });
        if (isFileDownload) {
            saveAs(blob, filename);
        }
        return file;
    } catch (error) {
        console.log(error);
    }
}


export const downloadPDFWithHighlights = async (pdfUrl: string, stroke: PdfHighlighterEntity[], solutionList: SolutionDetails[] | [], scale = 1, isFileDownload = true, isHighlighShow: boolean = true): Promise<File | undefined> => {
    try {
        const pdfBytes = await fetch(pdfUrl).then(res => res.arrayBuffer());
        const pdfDoc = await PDFDocument.load(pdfBytes);
        const pages = pdfDoc.getPages();


        // ---- Strokes ----
        const strokesByPage = stroke.reduce<Record<number, PdfHighlighterEntity[]>>((acc, s) => {
            const pageNum = 0;
            if (!acc[pageNum]) {
                acc[pageNum] = [];
            }
            acc[pageNum].push(s);
            return acc;
        }, {});

        const strokeOpacity = 0.5; // lower to make intersections lighter
        const strokeSegments = 1; // reduce from 100 to avoid overdraw

        for await (const [pageNumStr, pageStrokes] of Object.entries(strokesByPage)) {
            const page = pages[parseInt(pageNumStr)];
            if (!page) continue;

            const { width, height } = page.getSize();
            const rotation = page.getRotation().angle;

            for await (const s of pageStrokes) {

                const svgIconUrl = s.systemIcon;


                const absPoints = s.points.map((pt) =>
                    pt.x == null || pt.y == null
                        ? null
                        : getRotatedCoordinates(pt.x, pt.y, width, height, rotation)
                );

                const strokeWidth = s.width * scale;
                const { rgb: pdfStrokeColor } = hexToRgb(s.color);

                // === Draw stroke lines ===
                for (let i = 0; i < absPoints.length - 1; i++) {
                    const p1 = absPoints[i];
                    const p2 = absPoints[i + 1];
                    if (!p1 || !p2) continue;

                    const segments = interpolateLinePoints(p1, p2, strokeSegments);

                    if (isHighlighShow) {
                        for (let j = 0; j < segments.length - 1; j++) {
                            page.drawLine({
                                start: segments[j],
                                end: segments[j + 1],
                                color: pdfStrokeColor,
                                opacity: strokeOpacity ?? 1,
                                thickness: strokeWidth || 1,
                                lineCap: LineCapStyle.Projecting,
                            });
                        }
                    }
                }


                try {

                    // === ICON PLACEMENT 
                    if (s.points.length >= 2) {
                        // Usage inside your forEach stroke:
                        const bestSeg = getBestSegment(s.points);
                        if (bestSeg) {
                            const startAbs = getRotatedCoordinates(bestSeg.start.x, bestSeg.start.y, width, height, rotation);
                            const endAbs = getRotatedCoordinates(bestSeg.end.x, bestSeg.end.y, width, height, rotation);

                            const midX = (startAbs.x + endAbs.x) / 2;
                            const midY = (startAbs.y + endAbs.y) / 2;

                            const dx = endAbs.x - startAbs.x;
                            const dy = endAbs.y - startAbs.y;

                            const perpX = -dy;
                            const perpY = dx;
                            const lenPerp = Math.hypot(perpX, perpY);
                            const unitPerpX = perpX / lenPerp;
                            const unitPerpY = perpY / lenPerp;

                            const iconOffset = rotation === 0 ? 24 : 0;
                            const iconX = midX + unitPerpX * iconOffset;
                            const iconY = midY + unitPerpY * iconOffset;

                            if (s.solutionId && !IsStringNullEmptyOrUndefined(s.solutionId)) {
                                const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
                                const { rgb: pdfStrokeColor } = hexToRgb(s.color);
                                const solutionDetail = solutionList.find((item) => item._id === s.solutionId);
                                if (solutionDetail && !IsNullOrUndefined(solutionDetail)) {
                                    drawNumberIcon(page, {
                                        x: iconX,
                                        y: iconY,
                                        number: solutionDetail.solutionNo,
                                        font,
                                        size: 20,
                                        color: pdfStrokeColor,
                                        borderWidth: 2,
                                        rotation,
                                    });
                                }
                            } else {
                                const iconPngBytes = await svgUrlToPngBytes(svgIconUrl, s.color, 60, 60);
                                const iconImage = await pdfDoc.embedPng(iconPngBytes);
                                const iconDims = iconImage.scale(0.4);

                                page.drawImage(iconImage, {
                                    x: iconX - iconDims.width / 2,
                                    y: iconY - iconDims.height / 2,
                                    width: iconDims.width,
                                    height: iconDims.height,
                                    rotate: degrees(rotation),
                                });
                            }
                        }

                    }
                } catch (error) {
                    console.log(error);
                }
            };
        }

        const modifiedBytes: any = await pdfDoc.save();
        const blob = new Blob([modifiedBytes], { type: 'application/pdf' });
        const originalFilename = pdfUrl.split('/').pop() || 'document';
        const filename = originalFilename.replace('.pdf', '-with-highlights.pdf');
        const file = new File([blob], filename, { type: 'application/pdf' });
        if (isFileDownload) {
            saveAs(blob, filename);
        }
        return file
    } catch (error) {
        console.log(error);
    }
}


function arePointsEqual(a: PointsEntity[], b: PointsEntity[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((pt, i) => pt.x === b[i].x && pt.y === b[i].y);
}

export function getUpdatedPinRecords(
    initialRecords: MapEntity,
    newRecords: MapEntity
): MapEntity {
    const result: MapEntity = {};

    const allPageIds: string[] = Array.from(
        new Set([...Object.keys(initialRecords), ...Object.keys(newRecords)])
    );

    for (const pageId of allPageIds) {
        const newPins = newRecords[pageId] || [];
        const oldPins = initialRecords[pageId] || [];

        let hasChanges = false;

        const oldPinMap = new Map(oldPins.map(pin => [pin.solutionId, pin]));

        // Check for added or updated pins
        for (const newPin of newPins) {
            const oldPin = oldPinMap.get(newPin.solutionId);
            if (!oldPin) {
                hasChanges = true; // new pin added
                break;
            }

            if (
                newPin.systemId !== oldPin.systemId ||
                newPin.width !== oldPin.width ||
                !arePointsEqual(newPin.points, oldPin.points)
            ) {
                hasChanges = true; // pin updated
                break;
            }
        }

        // Check for deleted pins
        if (!hasChanges) {
            for (const oldPin of oldPins) {
                if (!newPins.find(p => p.solutionId === oldPin.solutionId)) {
                    hasChanges = true;
                    break;
                }
            }
        }

        if (hasChanges) {
            result[pageId] = newPins;
        }
    }

    return result;
}



export function getRemovedPinRecords(
    initialRecords: MapEntity,
    newRecords: MapEntity
): MapEntity {
    const removedRecords: MapEntity = {};

    for (const pageId in initialRecords) {
        const oldPins = initialRecords[pageId] || [];
        const newPins = newRecords[pageId] || [];

        const removedPins = oldPins.filter(oldPin => {
            return (
                !newPins.some(newPin => newPin.solutionId === oldPin.solutionId)
            );
        });

        if (removedPins.length > 0) {
            removedRecords[pageId] = removedPins;
        }
    }

    return removedRecords;
}


export const updateColorForCoordinations = (data: MapEntity): MapEntity => {
    let newData = data;

    Object.keys(newData).map((key) => {
        newData[key] = data[key].map((item) => {

            if (item.solutionId && !IsStringNullEmptyOrUndefined(item.solutionId)) {
                item.color = LINK_COLOR;
            } else {
                item.color = FINAL_COLOR;
            }
            return item;
        })
    })

    return newData;
}



export function mergeHighlightSetData(
    dbData: MapEntity,
    localData: MapEntity,
    deletedIds: string[] = []
): MapEntity {
    const merged: MapEntity = {};
    const deletedSet = new Set(deletedIds);

    const allPageIds: string[] = Array.from(
        new Set([
            ...Object.keys(dbData || {}),
            ...Object.keys(localData || {}),
        ])
    );

    for (const pageId of allPageIds) {
        const dbItems = Array.isArray(dbData?.[pageId]) ? dbData[pageId] : [];
        const localItems = Array.isArray(localData?.[pageId]) ? localData[pageId] : [];

        const dbMap = new Map(dbItems.map(item => [item.id, item]));
        const finalItems: any[] = [];

        for (const dbItem of dbItems) {
            if (deletedSet.has(dbItem.id)) {
                continue; // skip deleted
            }
            const localVersion = localItems.find(li => li.id === dbItem.id && !deletedSet.has(li.id));
            if (localVersion) {
                finalItems.push({ ...dbItem, ...localVersion });
            } else {
                finalItems.push(dbItem);
            }
        }

        for (const localItem of localItems) {
            if (deletedSet.has(localItem.id)) {
                continue; // skip deleted
            }
            if (!localItem.id || !dbMap.has(localItem.id)) {
                finalItems.push(localItem);
            }
        }

        if (finalItems.length > 0) {
            merged[pageId] = finalItems;
        }
    }

    return merged;
}






// export function mergeAddedUpdatedAndDeletedCoordinatesDetails(dbData: MapEntity, localData: MapEntity): { merged: MapEntity, deletedIds: string[] | [] } {
//     const merged: MapEntity = {};
//     const deletedIds: string[] = [];

//     const allPageIds: any = new Set([
//         ...Object.keys(dbData || {}),
//         ...Object.keys(localData || {}),
//     ]);

//     for (const pageId of allPageIds) {
//         const dbItems = dbData[pageId] || [];
//         const localItems = localData[pageId] || [];

//         const dbMap = new Map(dbItems.map(item => [item.id, item]));
//         const localMap = new Map(localItems.map(item => [item.id, item]));

//         const pageMerged: any[] = [];

//         for (const localItem of localItems) {
//             if (!localItem.id) {
//                 // new local item
//                 pageMerged.push(localItem);
//             } else if (dbMap.has(localItem.id)) {
//                 const dbItem = dbMap.get(localItem.id);

//                 const { ...dbClean } = dbItem;
//                 const { ...localClean } = localItem;

//                 if (JSON.stringify(dbClean) !== JSON.stringify(localClean)) {
//                     pageMerged.push(localItem); // updated
//                 }
//             } else {
//                 pageMerged.push(localItem);
//             }
//         }

//         if (pageMerged.length > 0) {
//             merged[pageId] = pageMerged;
//         }

//         for (const dbItem of dbItems) {
//             if (dbItem.id && !localMap.has(dbItem.id)) {
//                 deletedIds.push(dbItem.id);
//             }
//         }

//     }
//     return { merged, deletedIds };
// }

export function mergeAddedUpdatedAndDeletedCoordinatesDetails(
    dbData: MapEntity,
    localData: MapEntity
): { merged: MapEntity; deletedIds: string[] } {

    const merged: MapEntity = {};
    const deletedIds: string[] = [];

    // Fix: Convert Set → Array so TS can iterate
    const allPageIds = Array.from(
        new Set([
            ...Object.keys(dbData || {}),
            ...Object.keys(localData || {}),
        ])
    );

    for (const pageId of allPageIds) {
        const dbItems = dbData[pageId] || [];
        const localItems = localData[pageId] || [];

        const dbMap = new Map(dbItems.map((i) => [i.id, i]));
        const localMap = new Map(localItems.map((i) => [i.id, i]));

        const pageMerged: PdfHighlighterEntity[] = [];

        // ---- Merge new, updated, and unchanged ----
        for (const localItem of localItems) {
            if (!localItem.id) {
                // NEW item
                pageMerged.push(localItem);
                continue;
            }

            const dbItem = dbMap.get(localItem.id);

            if (!dbItem) {
                // Exists locally but not in DB → NEW
                pageMerged.push(localItem);
                continue;
            }

            // Compare objects for update detection
            if (JSON.stringify(localItem) !== JSON.stringify(dbItem)) {
                // UPDATED
                pageMerged.push(localItem);
            }
        }

        // ---- Deleted items ----
        for (const dbItem of dbItems) {
            if (dbItem.id && !localMap.has(dbItem.id)) {
                deletedIds.push(dbItem.id);
            }
        }

        if (pageMerged.length > 0) {
            merged[pageId] = pageMerged;
        }
    }

    return { merged, deletedIds };
}





export function compareDrawingSetDetails(dbRecords: AnnotationDrawingPlanEntity[], localRecords: AnnotationDrawingPlanEntity[]): AnnotationDrawingPlanEntity[] {
    const result: AnnotationDrawingPlanEntity[] = [];

    // Create a map for db records by drawingId for quick lookup
    const dbMap = new Map<string, AnnotationDrawingPlanEntity>();
    dbRecords.forEach(dbRecord => {
        dbMap.set(dbRecord.drawingId, dbRecord);
    });

    localRecords.forEach(localRecord => {
        const dbRecord = dbMap.get(localRecord.drawingId);

        if (!dbRecord) {
            // If the whole drawing is new, add entire localRecord
            result.push(localRecord);
            return;
        }

        // Compare PDFs within the same drawingId
        const dbPageIds = new Set(dbRecord.PDFs.map(pdf => pdf.pageId));

        // Find PDFs in local but not in db => new or updated PDFs
        const newOrUpdatedPDFs = localRecord.PDFs.filter(pdf => !dbPageIds.has(pdf.pageId));

        if (newOrUpdatedPDFs.length > 0) {
            result.push({
                drawingId: localRecord.drawingId,
                drawingName: localRecord.drawingName,
                PDFs: newOrUpdatedPDFs
            });
        }
    });

    return result;
}